import React from 'react';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Gamepad2, Star, Trophy, Zap, Flame, Rocket } from 'lucide-react';

export const GamesPage: React.FC = () => {
  return (
    <Layout>
      <PageHeader
        title="🎮 Game Zone! 🎮"
        subtitle="Play fun Tamil games & win stars! ⭐✨"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {/* Tamil Word Match */}
        <Card className="bg-gradient-to-br from-yellow-400 to-orange-500 text-white shadow-2xl hover:scale-105 transition-all duration-300 border-4 border-yellow-200 group">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-4 bg-white/30 rounded-2xl group-hover:bg-white/50 transition-all">
                <Gamepad2 className="w-12 h-12 text-red-300 animate-bounce" />
              </div>
              <CardTitle className="text-2xl font-black drop-shadow-lg">
                Word Match Magic 🧙‍♂️
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-extrabold mb-4 drop-shadow-lg">125</p>
            <p className="text-yellow-100 font-bold bg-white/20 px-4 py-2 rounded-xl inline-block mb-4">
              High Score
            </p>
            <p className="text-lg font-semibold mb-4">Match Tamil words with pictures!</p>
            <div className="flex items-center justify-center gap-2 text-2xl mb-2">
              <span className="text-3xl">வீடு</span> → 🏠
            </div>
            <button className="w-full bg-gradient-to-r from-red-500 to-pink-500 text-white font-black py-3 px-6 rounded-2xl shadow-xl hover:scale-105 transition-all flex items-center justify-center gap-2">
              <Zap className="w-6 h-6" />
              Play Now!
            </button>
          </CardContent>
        </Card>

        {/* Tamil Quiz Challenge */}
        <Card className="bg-gradient-to-br from-emerald-400 to-green-500 text-white shadow-2xl hover:scale-105 transition-all duration-300 border-4 border-emerald-200 group">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-4 bg-white/30 rounded-2xl group-hover:bg-white/50 transition-all">
                <Trophy className="w-12 h-12 text-yellow-300 animate-pulse" />
              </div>
              <CardTitle className="text-2xl font-black drop-shadow-lg">
                Quiz Master 🏆
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-extrabold mb-4 drop-shadow-lg">98%</p>
            <p className="text-emerald-100 font-bold bg-white/20 px-4 py-2 rounded-xl inline-block mb-4">
              Best Score
            </p>
            <ul className="space-y-2 mb-6 text-sm">
              <li className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-300 fill-yellow-300" />
                15 quizzes won
              </li>
              <li className="flex items-center gap-2">
                <Flame className="w-5 h-5 text-orange-400" />
                3 day streak!
              </li>
            </ul>
            <button className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-black py-3 px-6 rounded-2xl shadow-xl hover:scale-105 transition-all flex items-center justify-center gap-2">
              <Rocket className="w-6 h-6" />
              Challenge Now!
            </button>
          </CardContent>
        </Card>

        {/* Typing Adventure */}
        <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white shadow-2xl hover:scale-105 transition-all duration-300 border-4 border-purple-200 group">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-4 bg-white/30 rounded-2xl group-hover:bg-white/50 transition-all">
                <Gamepad2 className="w-12 h-12 text-pink-300 animate-spin" />
              </div>
              <CardTitle className="text-2xl font-black drop-shadow-lg">
                Type Race 🏎️
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-extrabold mb-4 drop-shadow-lg">47</p>
            <p className="text-purple-100 font-bold bg-white/20 px-4 py-2 rounded-xl inline-block mb-4">
              WPM (Words Per Minute)
            </p>
            <div className="text-center mb-6">
              <div className="inline-flex bg-black/30 px-6 py-3 rounded-2xl mb-3">
                <span className="text-2xl font-mono">கத்தலில்</span>
              </div>
              <p className="text-purple-200 text-sm">Type super fast!</p>
            </div>
            <button className="w-full bg-gradient-to-r from-pink-500 to-purple-600 text-white font-black py-3 px-6 rounded-2xl shadow-xl hover:scale-105 transition-all flex items-center justify-center gap-2">
              <Zap className="w-6 h-6" />
              Race Now!
            </button>
          </CardContent>
        </Card>
      </div>

      {/* Leaderboard & Daily Challenge */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="bg-gradient-to-br from-orange-500 to-red-600 text-white shadow-2xl border-4 border-orange-200">
          <CardHeader>
            <CardTitle className="text-2xl font-black flex items-center gap-3">
              <Trophy className="w-10 h-10" />
              Top Players 🔥
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-white/20 rounded-2xl">
                <span className="font-bold text-xl">1st 🥇</span>
                <span className="text-2xl font-black">ராகுல்</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-white/20 rounded-2xl">
                <span className="font-bold text-xl">2nd 🥈</span>
                <span className="text-xl font-bold">அனு</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-white/20 rounded-2xl">
                <span className="font-bold text-lg">3rd 🥉</span>
                <span className="text-lg font-semibold">நிதி</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-2xl border-4 border-blue-200">
          <CardHeader>
            <CardTitle className="text-2xl font-black flex items-center gap-3">
              <Flame className="w-10 h-10 animate-pulse" />
              Daily Challenge
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-3xl font-extrabold mb-4 drop-shadow-lg">50 Stars</p>
            <div className="bg-black/30 p-6 rounded-3xl mb-6">
              <p className="text-2xl mb-4 font-bold">Type these words:</p>
              <div className="grid grid-cols-2 gap-4 text-2xl font-mono">
                <span>மாமா</span>
                <span>பாபா</span>
              </div>
            </div>
            <button className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-black py-4 px-8 rounded-2xl text-lg shadow-2xl hover:scale-105 transition-all">
              Win Daily Stars! ⭐⭐⭐
            </button>
          </CardContent>
        </Card>
      </div>

      {/* Hero Call to Action */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 text-white p-10 rounded-3xl shadow-2xl border-4 border-white/30 text-center max-w-5xl mx-auto">
        <Gamepad2 className="w-24 h-24 mx-auto mb-6 text-yellow-300 animate-bounce" />
        <h2 className="text-5xl font-black mb-6 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent drop-shadow-2xl">
          Level Up Your Tamil! 🚀
        </h2>
        <p className="text-2xl mb-10 font-bold max-w-3xl mx-auto leading-relaxed">
          Play games, win stars, beat your friends, and become Tamil Superstar! 
          Every game makes you smarter! 🧠✨
        </p>
        <div className="flex flex-col sm:flex-row gap-6 justify-center">
          <button className="bg-white text-purple-600 font-black py-5 px-12 rounded-3xl text-xl shadow-2xl hover:scale-105 transition-all flex items-center gap-3 mx-auto sm:mx-0">
            <Rocket className="w-8 h-8" />
            Play All Games
          </button>
          <button className="bg-yellow-400 text-black font-black py-5 px-12 rounded-3xl text-xl shadow-2xl hover:scale-105 transition-all flex items-center gap-3 mx-auto sm:mx-0">
            <Trophy className="w-8 h-8" />
            View Leaderboard
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default GamesPage;
